// Client behavioural analytics.
//
// trackEvent() buffers UX events and flushes them in batches to
// POST /api/events/client (server allowlist is the security boundary — see
// CLIENT_EVENT_TYPES in the worker). Best-effort throughout: never throws,
// never blocks a render, drops on failure rather than retrying forever.
import { api, getBearerToken } from "./api";

type Evt = { type: string; props?: Record<string, unknown>; ts: number };

const buf: Evt[] = [];
const MAX_BUF = 200; // cap so a logged-out / offline session can't grow unbounded
const BATCH = 50; // matches the worker's CLIENT_EVENT_BATCH_MAX
const FLUSH_MS = 6000;

let flushTimer: ReturnType<typeof setTimeout> | null = null;
let started = false;

const scheduleFlush = () => {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => { flushTimer = null; void flush(); }, FLUSH_MS);
};

export const trackEvent = (type: string, props?: Record<string, unknown>): void => {
  buf.push({ type, props, ts: Date.now() });
  if (buf.length > MAX_BUF) buf.splice(0, buf.length - MAX_BUF);
  scheduleFlush();
};

export const flush = async (): Promise<void> => {
  if (!getBearerToken() || buf.length === 0) return; // not authed yet — keep buffered
  const batch = buf.splice(0, BATCH);
  try {
    await api("/events/client", { method: "POST", body: JSON.stringify({ events: batch }) });
  } catch {
    // swallow — analytics must never surface an error to the app
  }
  if (buf.length > 0) scheduleFlush(); // drain any remainder
};

// Flush on tab-hide / app-background so a session's tail isn't lost. Registered
// once; safe to call from app boot.
export const startAnalytics = (): void => {
  if (started || typeof document === "undefined") return;
  started = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush();
  });
  window.addEventListener("pagehide", () => { void flush(); });
};

// ---- viewport "*_viewed" helper ----
// One shared IntersectionObserver fires a *_viewed event the first time an
// element crosses 60% visible, deduped by key for the session. The HUD attaches
// elements via observeView; resetViewDedup() lets a fresh screen re-arm them.
const seen = new Set<string>();
const meta = new WeakMap<Element, { key: string; name: string; props?: Record<string, unknown> }>();
let io: IntersectionObserver | null = null;

const ensureIO = (): IntersectionObserver | null => {
  if (io || typeof IntersectionObserver === "undefined") return io;
  io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      const m = meta.get(e.target);
      if (!m || seen.has(m.key)) continue;
      seen.add(m.key);
      trackEvent(m.name, m.props);
      io!.unobserve(e.target);
    }
  }, { threshold: 0.6 });
  return io;
};

export const observeView = (
  el: Element | null,
  key: string,
  name: string,
  props?: Record<string, unknown>,
): (() => void) => {
  if (!el || seen.has(key)) return () => {};
  const o = ensureIO();
  if (!o) return () => {};
  meta.set(el, { key, name, props });
  o.observe(el);
  return () => { o.unobserve(el); };
};

export const resetViewDedup = (): void => { seen.clear(); };
